import React from 'react';
import { Check, X } from 'lucide-react';
import {
  formatMeasure,
  formatOmdDate,
  type OmdLogistics,
  type OmdProduct,
} from '../../../utils/omdProductView';
import type { OmdPanelSettings } from '../../../utils/omdPanelSettings';
import { OmdDetailGrid } from './OmdDetailGrid';

function BoolPill({ label, value }: { label: string; value: boolean | undefined | null }) {
  if (value == null) return null;
  const ok = value;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
        ok ? 'bg-green-50 text-green-800 border border-green-100' : 'bg-red-50 text-red-800 border border-red-100'
      }`}
    >
      {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
      {label}
    </span>
  );
}

export function OmdDimensionsCards({ logistics }: { logistics?: OmdLogistics }) {
  if (!logistics) return null;
  const cards = [
    { label: 'Höhe', value: formatMeasure(logistics.measureA) },
    { label: 'Breite', value: formatMeasure(logistics.measureB) },
    { label: 'Tiefe', value: formatMeasure(logistics.measureC) },
    { label: 'Gewicht', value: formatMeasure(logistics.weight) },
  ].filter((c) => c.value);
  if (cards.length === 0) return null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {cards.map((c) => (
        <div key={c.label} className="rounded-lg border border-app-border bg-app-surface-muted px-3 py-2">
          <p className="text-xs font-medium text-app-text-muted">{c.label}</p>
          <p className="text-sm font-semibold text-app-text mt-0.5">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

export function OmdComplianceSection({
  product,
  settings,
}: {
  product: OmdProduct;
  settings: OmdPanelSettings;
}) {
  const log = product.logistics;
  if (!log) return null;

  const hasPills =
    log.hazardousMaterial != null || log.ubaListRelevant != null || log.exportable != null;
  const rows = [
    { label: 'REACH', value: log.reachInfo },
    { label: 'REACH-Datum', value: formatOmdDate(log.reachDate) },
    { label: 'WEEE-Nr.', value: log.weeeNumber },
    { label: 'LUCID-Nr.', value: log.lucidNumber },
    { label: 'Entsorgungsdienstleister', value: log.packagingDisposalProvider },
    { label: 'Herkunftsland', value: log.countryOfOrigin },
    { label: 'Warennummer', value: log.commodityNumber },
  ];

  const hasRows = rows.some((r) => r.value) || hasPills;
  if (!hasRows) return null;

  return (
    <div className="space-y-3">
      {hasPills && (
        <div className="flex flex-wrap gap-2">
          <BoolPill label="Gefahrgut" value={log.hazardousMaterial} />
          <BoolPill label="UBA-Liste" value={log.ubaListRelevant} />
          <BoolPill label="UBA-konform" value={log.ubaListConform} />
          <BoolPill label="Exportierbar" value={log.exportable} />
        </div>
      )}
      <OmdDetailGrid rows={rows} settings={settings} />
    </div>
  );
}

export function OmdSparePartList({ product }: { product: OmdProduct }) {
  const lists = product.sparepartlists;
  const normalized = Array.isArray(lists) ? lists : lists ? [lists] : [];
  if (normalized.length === 0) return null;

  return (
    <div className="space-y-4">
      {normalized.map((list, li) => (
        <div key={li} className="rounded-lg border border-app-border overflow-hidden">
          {list.listNumber && (
            <p className="px-3 py-2 bg-app-surface-muted text-xs font-semibold text-app-text-muted border-b border-app-border">
              Liste {list.listNumber}
            </p>
          )}
          <ul className="divide-y divide-app-border">
            {(list.rows ?? []).map((row, ri) => (
              <li key={ri} className="px-3 py-2 text-sm flex gap-3">
                {row.position != null && (
                  <span className="text-app-text-subtle tabular-nums shrink-0 w-8">{row.position}</span>
                )}
                <span className="text-app-text flex-1">{row.text || row.linkedProduct || '-'}</span>
                {row.pricegroup && (
                  <span className="text-xs text-app-text-subtle shrink-0">{row.pricegroup}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
